import * as actionTypes from "../constants";
import { checkForConcurrencyError } from "../utils/checks";

import type { AnyAction } from "redux";
import type {
  ChoiceListDataItem,
  ChoiceListDefaultValue,
} from "../scenes/ChoiceListsSettings/models/choiceListsSettings";
import type { ChoiceListsSettingsState } from "../scenes/ChoiceListsSettings/models/choiceListsSettingsState";
import type {
  ConversionFactorItem,
  ProviderConversionFactorError,
} from "../scenes/ChoiceListsSettings/models/providerConversionFactors";

const INITIAL_STATE: ChoiceListsSettingsState = {
  defaultValues: [],
  errorText: "",
  hideInactiveItems: true,
  items: [],
  loading: false,
  originalItems: [],
  providerConversionFactors: {
    error: null,
    hasChanges: false,
    list: {},
    loading: false,
  },
};

export const choiceListsSettingsReducer = (state = INITIAL_STATE, action: AnyAction) => {
  switch (action.type) {
    case actionTypes.CHOICE_LISTS_SETTINGS_LOADING:
      return handleSetLoading(state, action.payload);
    case actionTypes.CHOICE_LISTS_SETTINGS_LIST_LOADED:
      return handleChoiceListLoaded(state, action.payload);
    case actionTypes.CHOICE_LISTS_SETTINGS_CHANGE_ITEM:
      return handleChangeDataItem(state, action.payload);
    case actionTypes.CHOICE_LISTS_SETTINGS_CONFIRM_CHANGE_ITEM:
      return handleConfirmChangeItem(state, action.payload);
    case actionTypes.CHOICE_LISTS_SETTINGS_DISCARD_CHANGE_ITEM:
      return handleDiscardChangeItem(state, action.payload);
    case actionTypes.CHOICE_LISTS_SETTINGS_REMOVE_ITEM:
      return handleRemoveItem(state, action.payload);
    case actionTypes.CHOICE_LISTS_SETTINGS_CREATE_ITEM:
      return handleCreateItem(state, action.payload);
    case actionTypes.CHOICE_LISTS_SETTINGS_CONFIRM_CREATE_ITEM:
      return handleConfirmCreate(state, action.payload);
    case actionTypes.CHOICE_LISTS_SETTINGS_DISCARD_CREATE_ITEM:
      return handleDiscardCreate(state, action.payload);
    case actionTypes.CHOICE_LISTS_SETTINGS_DISCARD_ALL_CHANGES:
    case actionTypes.SETTINGS_CONFIRM_DISCARD_DIRTY_CHANGES:
      return handleDiscardAllChanges(state);
    case actionTypes.CHOICE_LISTS_SETTINGS_SET_ERROR:
      return handleSetError(state, action.payload);
    case actionTypes.CHOICE_LISTS_SETTINGS_TOGGLE_HIDE_INACTIVE_ITEMS:
      return handleToogleHideInactiveItems(state);
    case actionTypes.GET_PROVIDER_CONVERSION_FACTORS_COMPLETED:
      return handleProviderConversionFactorsCompleted(state, action.payload);
    case actionTypes.PROVIDER_CONVERSION_FACTORS_SET_HAS_CHANGES:
      return handleProviderConversionFactorsHasChanges(state, action.payload);
    case actionTypes.CHANGE_PROVIDER_ITEM_CONVERSION_FACTORS_COUNT:
      return handleChangeProviderItemConversionFactorCount(state, action.payload);
    case actionTypes.PROVIDER_CONVERSION_FACTORS_LOADING:
      return handleProviderConversionFactorsLoading(state, action.payload);
    case actionTypes.PROVIDER_CONVERSION_FACTORS_ERROR:
      return handleProviderConversionFactorsError(state, action.payload);

    default:
      return state;
  }
};

const handleSetLoading = (
  state: ChoiceListsSettingsState,
  loading: boolean,
): ChoiceListsSettingsState => ({ ...state, loading });

const handleChoiceListLoaded = (
  state: ChoiceListsSettingsState,
  payload: { choiceList: ChoiceListDataItem[]; defaultValues: ChoiceListDefaultValue[] },
): ChoiceListsSettingsState => ({
  ...state,
  defaultValues: [...payload.defaultValues],
  errorText: "",
  items: [...payload.choiceList],
  originalItems: [...payload.choiceList],
  providerConversionFactors: {
    ...state.providerConversionFactors,
    error: null,
    hasChanges: false,
    list: {},
  },
});

const addConversionFactorCount = (newItem: ChoiceListDataItem, oldItem: ChoiceListDataItem) =>
  !("ConversionFactorCount" in newItem) && "ConversionFactorCount" in oldItem ?
    {
      ...newItem,
      ConversionFactorCount: oldItem.ConversionFactorCount,
    }
  : newItem;

const handleChangeDataItem = (
  state: ChoiceListsSettingsState,
  dataItem: ChoiceListDataItem,
): ChoiceListsSettingsState => ({
  ...state,
  items: state.items.map((item) =>
    item.ViewId === dataItem.ViewId ? addConversionFactorCount(dataItem, item) : item,
  ),
});

const handleChangeProviderItemConversionFactorCount = (
  state: ChoiceListsSettingsState,
  payload: { providerId: string; count: number },
): ChoiceListsSettingsState => ({
  ...state,
  items: state.items.map((item) =>
    item.ViewId === payload.providerId ? { ...item, ConversionFactorCount: payload.count } : item,
  ),
  originalItems: state.originalItems.map((item) =>
    item.ViewId === payload.providerId ? { ...item, ConversionFactorCount: payload.count } : item,
  ),
});

const handleRemoveItem = (
  state: ChoiceListsSettingsState,
  dataItem: ChoiceListDataItem,
): ChoiceListsSettingsState => ({
  ...state,
  items: state.items.filter((item) => item.ViewId !== dataItem.ViewId),
  originalItems: state.originalItems.filter((item) => item.ViewId !== dataItem.ViewId),
  errorText: "",
});

const handleCreateItem = (
  state: ChoiceListsSettingsState,
  dataItem: ChoiceListDataItem,
): ChoiceListsSettingsState => ({
  ...state,
  items: [dataItem, ...state.items],
  errorText: "",
});

const handleConfirmCreate = (
  state: ChoiceListsSettingsState,
  payload: { createdItemViewId: string; dataItem: ChoiceListDataItem },
): ChoiceListsSettingsState => {
  const newItem = { ...payload.dataItem, createdItemViewId: "", inEdit: undefined };

  const newState = {
    ...state,
    items: state.items.map((item) =>
      item.ViewId === payload.createdItemViewId ? addConversionFactorCount(newItem, item) : item,
    ),
    originalItems: [{ ...newItem }, ...state.originalItems],
    errorText: "",
  };

  return newState;
};

const handleDiscardCreate = (
  state: ChoiceListsSettingsState,
  dataItem: ChoiceListDataItem,
): ChoiceListsSettingsState => ({
  ...state,
  items: state.items.filter((item) => item.ViewId !== dataItem.ViewId),
  errorText: "",
});

const handleConfirmChangeItem = (
  state: ChoiceListsSettingsState,
  dataItem: ChoiceListDataItem,
): ChoiceListsSettingsState => {
  const newItem = { ...dataItem, inEdit: undefined };

  const newState = {
    ...state,
    items: state.items.map((item) =>
      item.ViewId === dataItem.ViewId ? addConversionFactorCount(newItem, item) : item,
    ),
    originalItems: state.originalItems.map((item) =>
      item.ViewId === dataItem.ViewId ? { ...addConversionFactorCount(newItem, item) } : item,
    ),
    errorText: "",
  };

  return newState;
};

const handleDiscardChangeItem = (
  state: ChoiceListsSettingsState,
  dataItem: ChoiceListDataItem,
): ChoiceListsSettingsState => {
  const original = state.originalItems.find((item) => item.ViewId === dataItem.ViewId);

  if (!original) {
    return {
      ...state,
      items: state.items.filter((item) => item.ViewId !== dataItem.ViewId),
      errorText: "",
    };
  }

  const newState = {
    ...state,
    items: state.items.map((item) => (item.ViewId === original.ViewId ? { ...original } : item)),
    errorText: "",
  };

  return newState;
};

const handleDiscardAllChanges = (state: ChoiceListsSettingsState): ChoiceListsSettingsState => ({
  ...state,
  items: [...state.originalItems],
  errorText: "",
});

const handleSetError = (
  state: ChoiceListsSettingsState,
  errorText: string,
): ChoiceListsSettingsState => {
  const concurrencyError = checkForConcurrencyError(errorText);

  return {
    ...state,
    errorText: concurrencyError ? "" : errorText,
  };
};

const handleToogleHideInactiveItems = (
  state: ChoiceListsSettingsState,
): ChoiceListsSettingsState => ({
  ...state,
  hideInactiveItems: !state.hideInactiveItems,
});

const handleProviderConversionFactorsCompleted = (
  state: ChoiceListsSettingsState,
  payload: { providerId: string; list: ConversionFactorItem[]; concurrencyVersion?: string },
): ChoiceListsSettingsState => ({
  ...state,
  providerConversionFactors: {
    ...state.providerConversionFactors,
    list: {
      ...state.providerConversionFactors.list,
      [payload.providerId]: payload.list,
    },
  },
});

const handleProviderConversionFactorsHasChanges = (
  state: ChoiceListsSettingsState,
  payload: boolean,
): ChoiceListsSettingsState => ({
  ...state,
  providerConversionFactors: {
    ...state.providerConversionFactors,
    hasChanges: payload,
  },
});

const handleProviderConversionFactorsLoading = (
  state: ChoiceListsSettingsState,
  payload: boolean,
): ChoiceListsSettingsState => ({
  ...state,
  providerConversionFactors: {
    ...state.providerConversionFactors,
    loading: payload,
  },
});

const handleProviderConversionFactorsError = (
  state: ChoiceListsSettingsState,
  error: ProviderConversionFactorError | null,
): ChoiceListsSettingsState => ({
  ...state,
  providerConversionFactors: {
    ...state.providerConversionFactors,
    error,
  },
});
